/**
 * Product Personalisation Handler
 * Handles adding products with personalisation add-ons to cart
 * Groups items together using _groupUID property
 */

(function() {
  'use strict';

  window.ProductPersonalisation = {
    // Helper function to extract property value from cart item properties
    // Handles both array format [{name: 'key', value: 'value'}] and object format {key: 'value'}
    getPropertyValue: function(properties, key) {
      if (!properties) return '';

      if (Array.isArray(properties)) {
        for (const prop of properties) {
          const propName = prop.name || prop.first;
          if (propName === key) {
            return prop.value || prop.last || '';
          }
        }
      } else if (typeof properties === 'object') {
        return properties[key] || '';
      }

      return '';
    },

    // Helper function to get item metadata from cart item
    getItemMetadata: function(item) {
      const groupUID = this.getPropertyValue(item.properties, '_groupUID');
      const isMain = this.getPropertyValue(item.properties, '_isMainItem') === 'true';
      const isAddon = this.getPropertyValue(item.properties, '_isAddon') === 'true';

      return { groupUID, isMain, isAddon };
    },

    getProductSection: function(form) {
      return form.closest('[data-section-type="product"]') || form.closest('.product-section');
    },

    init: function() {
      document.querySelectorAll('[data-section-type="product"]').forEach(section => {
        const form = section.querySelector('.product-single__form');
        if (!form || form.classList.contains('personalisation-loaded')) return;

        const hasPersonalisation = section.querySelector('.product-addon-container-gm') ||
                                   section.querySelector('.product-academy-personalisation');

        if (!hasPersonalisation) return;

        form.classList.add('personalisation-loaded');
        this.initState();
        this.bindEvents(section);
        this.bindFormSubmit(form, section);
      });
    },

    initState: function() {
      window.personalisationState = {
        player: '',
        badge: 'no',
        sponsor: 'no',
        naam: '',
        nummer: '',
        initials: ''
      };
    },

    bindEvents: function(section) {
      section.querySelectorAll('.player-select').forEach(select => {
        select.addEventListener('change', () => {
          window.personalisationState.player = select.value.trim();
        });
      });

      section.querySelector('.naam-input')?.addEventListener('input', e => {
        window.personalisationState.naam = e.target.value.trim();
      });

      section.querySelector('.nummer-input')?.addEventListener('input', e => {
        window.personalisationState.nummer = e.target.value.trim();
      });

      section.querySelector('#initials')?.addEventListener('input', e => {
        window.personalisationState.initials = e.target.value.trim();
      });

      section.querySelectorAll('.product-addon-buttons').forEach(buttonGroup => {
        buttonGroup.addEventListener('click', event => {
          const clicked = event.target;
          if (!clicked.classList.contains('product-addon-button')) return;

          const value = clicked.getAttribute('data-value');
          buttonGroup.querySelectorAll('.product-addon-button').forEach(btn => btn.classList.remove('selected'));
          clicked.classList.add('selected');
          buttonGroup.setAttribute('data-selected', value);

          if (buttonGroup.classList.contains('initials-toggle-buttons')) return;

          const optionType = buttonGroup.getAttribute('data-option') || 'badge';
          window.personalisationState[optionType] = value;
        });
      });

      section.querySelectorAll('.personalisation-button').forEach(button => {
        button.addEventListener('click', () => {
          section.querySelectorAll('.personalisation-button').forEach(btn => btn.classList.remove('selected'));
          button.classList.add('selected');

          const target = button.getAttribute('data-target');
          section.querySelector('.product-addon-player-item')?.classList.add('hidden');
          section.querySelector('.product-addon-naamnummer-container')?.classList.add('hidden');

          if (target === 'player') {
            section.querySelector('.product-addon-player-item')?.classList.remove('hidden');
          } else if (target === 'naam-nummer') {
            section.querySelector('.product-addon-naamnummer-container')?.classList.remove('hidden');
          }
        });
      });
    },

    bindFormSubmit: function(form, section) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const addToCartBtn = form.querySelector('[data-add-to-cart]');
        if (!addToCartBtn) return false;

        const defaultBtnText = addToCartBtn.querySelector('[data-add-to-cart-text]')?.dataset?.defaultText ||
          addToCartBtn.textContent.trim();

        addToCartBtn.disabled = true;
        const addToCartTextEl = addToCartBtn.querySelector('[data-add-to-cart-text]');
        if (addToCartTextEl) {
          addToCartTextEl.textContent = 'Toevoegen...';
        } else {
          addToCartBtn.textContent = 'Toevoegen...';
        }

        try {
          await this.addItemsToCart(form, section);
        } catch (err) {
          console.error('❌ Fout bij toevoegen:', err);
          alert('Er is iets mis gegaan bij het toevoegen van de items.');
        } finally {
          addToCartBtn.disabled = false;
          if (addToCartTextEl) {
            addToCartTextEl.textContent = defaultBtnText;
          } else {
            addToCartBtn.textContent = defaultBtnText;
          }
        }

        return false;
      }, true);
    },

    getMainProductDetails: function(section) {
      const mainProductTitle = section.getAttribute('data-product-title') || 'Onbekend Shirt';
      const variantId = parseInt(section.querySelector('select[name="id"]')?.value);
      const selectedVariant = window.meta?.product?.variants?.find(v => v.id === variantId);

      return {
        model: mainProductTitle,
        size: selectedVariant?.public_title || selectedVariant?.name || 'Unknown'
      };
    },

    generateGroupUID: function() {
      return `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    },

    /**
     * Returns an error message when a selected add-on is missing its variant ID,
     * or null when all required add-on products are configured.
     */
    validateSelectedAddons: function(section, selections) {
      const missingLabels = [];

      if (selections.badge === 'yes') {
        const badgeProductId = section.querySelector('#badge-product-addon')?.dataset?.productId;
        if (!badgeProductId) missingLabels.push('Badge');
      }

      if (selections.sponsor === 'yes') {
        const sponsorProductId = section.querySelector('#sponsor-sleeve-product-addon')?.dataset?.productId;
        if (!sponsorProductId) missingLabels.push('Sponsor mouw');
      }

      if (selections.activeMode === 'player' && selections.player) {
        const playerProductId = section.querySelector('.product-addon-player-item')?.dataset?.productId;
        if (!playerProductId) missingLabels.push('Speler personalisatie');
      }

      if (selections.activeMode === 'naam-nummer') {
        if (selections.naam) {
          const naamProductId = section.querySelector('.naam-input')?.dataset?.productId;
          if (!naamProductId) missingLabels.push('Naam personalisatie');
        }
        if (selections.nummer) {
          const nummerProductId = section.querySelector('.nummer-input')?.dataset?.productId;
          if (!nummerProductId) missingLabels.push('Nummer personalisatie');
        }
      }

      if (selections.initialsToggle === 'yes' && selections.initials) {
        const initialsProductId = section.querySelector('.product-academy-personalisation-initials-input')?.dataset?.productId;
        if (!initialsProductId) missingLabels.push('Initialen personalisatie');
      }

      if (missingLabels.length === 0) return null;

      return `De volgende add-ons zijn niet beschikbaar: ${missingLabels.join(', ')}. Neem contact op met de winkel.`;
    },

    addItemsToCart: async function(form, section) {
      await this.cleanupOrphanedAddons();

      const activeMode = section.querySelector('.personalisation-button.selected')
        ?.getAttribute('data-target') || 'none';

      const player = section.querySelector('.player-select')?.value.trim() || '';
      const naam = section.querySelector('.naam-input')?.value.trim() || '';
      const nummer = section.querySelector('.nummer-input')?.value.trim() || '';
      const initials = section.querySelector('#initials')?.value.trim() || '';

      const badge = section.querySelector('#badge-product-addon .product-addon-buttons')
        ?.getAttribute('data-selected') || 'no';

      const sponsor = section.querySelector('#sponsor-sleeve-product-addon .product-addon-buttons')
        ?.getAttribute('data-selected') || 'no';

      const initialsToggle = section.querySelector('.initials-toggle-buttons .product-academy-personalisation-button.selected')?.dataset?.value;

      if (initialsToggle === 'yes' && !initials) {
        alert('Vul je initialen in om verder te gaan.');
        return;
      }

      const validationError = this.validateSelectedAddons(section, {
        badge,
        sponsor,
        activeMode,
        player,
        naam,
        nummer,
        initialsToggle,
        initials
      });

      if (validationError) {
        alert(validationError);
        return;
      }

      if (activeMode === 'player') {
        const naamEl = section.querySelector('.naam-input');
        const nummerEl = section.querySelector('.nummer-input');
        if (naamEl) naamEl.value = '';
        if (nummerEl) nummerEl.value = '';
      } else if (activeMode === 'naam-nummer') {
        const playerSel = section.querySelector('.player-select');
        if (playerSel) playerSel.value = '';
      }

      const productDetails = this.getMainProductDetails(section);
      const shirtVariantId = form.querySelector('select[name="id"]')?.value;

      if (!shirtVariantId) {
        throw new Error('No variant selected');
      }

      const groupUID = this.generateGroupUID();
      const itemsToAdd = [];

      const mainItemProperties = {
        Model: productDetails.model,
        Size: productDetails.size,
        _groupUID: groupUID,
        _isMainItem: 'true'
      };

      if (activeMode === 'player' && player) {
        mainItemProperties.Speler = player;
      } else if (activeMode === 'naam-nummer') {
        if (naam) mainItemProperties.Naam = naam;
        if (nummer) mainItemProperties.Nummer = nummer;
      }

      if (badge === 'yes') {
        mainItemProperties.Badge = 'Eredivisie Badge';
      }
      if (sponsor === 'yes') {
        mainItemProperties.Sponsor = 'Sponsor Mouw';
      }
      if (initialsToggle === 'yes' && initials) {
        mainItemProperties['Initialen/nummer'] = initials;
      }

      itemsToAdd.push({
        id: parseInt(shirtVariantId),
        quantity: 1,
        properties: mainItemProperties
      });

      if (badge === 'yes') {
        const badgeProductId = section.querySelector('#badge-product-addon')?.dataset?.productId;
        itemsToAdd.push({
          id: parseInt(badgeProductId),
          quantity: 1,
          properties: {
            _groupUID: groupUID,
            _isAddon: 'true',
            _addonType: 'badge'
          }
        });
      }

      if (sponsor === 'yes') {
        const sponsorProductId = section.querySelector('#sponsor-sleeve-product-addon')?.dataset?.productId;
        itemsToAdd.push({
          id: parseInt(sponsorProductId),
          quantity: 1,
          properties: {
            _groupUID: groupUID,
            _isAddon: 'true',
            _addonType: 'sponsor'
          }
        });
      }

      if (activeMode === 'player' && player) {
        const playerProductId = section.querySelector('.product-addon-player-item')?.dataset?.productId;
        itemsToAdd.push({
          id: parseInt(playerProductId),
          quantity: 1,
          properties: {
            _groupUID: groupUID,
            _isAddon: 'true',
            _addonType: 'player'
          }
        });
      } else if (activeMode === 'naam-nummer') {
        if (naam) {
          const naamProductId = section.querySelector('.naam-input')?.dataset?.productId;
          itemsToAdd.push({
            id: parseInt(naamProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'naam'
            }
          });
        }
        if (nummer) {
          const nummerProductId = section.querySelector('.nummer-input')?.dataset?.productId;
          itemsToAdd.push({
            id: parseInt(nummerProductId),
            quantity: 1,
            properties: {
              _groupUID: groupUID,
              _isAddon: 'true',
              _addonType: 'nummer'
            }
          });
        }
      }

      if (initialsToggle === 'yes' && initials) {
        const initialsProductId = section.querySelector('.product-academy-personalisation-initials-input')?.dataset?.productId;
        itemsToAdd.push({
          id: parseInt(initialsProductId),
          quantity: 1,
          properties: {
            _groupUID: groupUID,
            _isAddon: 'true',
            _addonType: 'initials'
          }
        });
      }

      console.log('[ProductPersonalisation] Adding items to cart:', itemsToAdd);

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.description || 'Cart addition failed');
      }

      this.openCartDrawerAndRefresh();
    },

    openCartDrawerAndRefresh: function() {
      const cartButton = document.querySelector('.js-drawer-open-cart');
      if (cartButton) {
        cartButton.click();
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        }, 1500);
      } else {
        window.location.href = '/cart';
      }
    },

    sortCartItems: function() {
      const cartContainer = document.querySelector('.cart__items, [data-products]');
      if (!cartContainer) return;

      const items = Array.from(cartContainer.children);
      if (items.length === 0) return;

      const mainItems = [];
      const addonMap = new Map();
      const ungroupedItems = [];

      items.forEach(item => {
        const groupUID = item.getAttribute('data-group-uid');
        const isMain = item.classList.contains('cart__item--main');
        const isAddon = item.classList.contains('cart__item--addon');

        if (isMain && groupUID) {
          mainItems.push({ item, groupUID });
        } else if (isAddon && groupUID) {
          if (!addonMap.has(groupUID)) {
            addonMap.set(groupUID, []);
          }
          addonMap.get(groupUID).push(item);
        } else {
          ungroupedItems.push(item);
        }
      });

      cartContainer.innerHTML = '';

      mainItems.forEach(({ item, groupUID }) => {
        cartContainer.appendChild(item);
        const addons = addonMap.get(groupUID) || [];
        addons.forEach(addon => cartContainer.appendChild(addon));
      });

      ungroupedItems.forEach(item => cartContainer.appendChild(item));
    },

    initCartRemoval: function() {
      const existingHandler = document._cartRemoveHandler;
      if (existingHandler) {
        document.removeEventListener('click', existingHandler, true);
      }

      const existingQtyHandler = document._cartQtyHandler;
      if (existingQtyHandler) {
        document.removeEventListener('click', existingQtyHandler, true);
      }

      document._cartRemoveHandler = async (e) => {
        const link = e.target.closest('.cart__remove-link');
        if (!link) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        const itemKey = link.getAttribute('data-item-key');
        const groupUID = link.getAttribute('data-group-uid');

        if (!itemKey) return;

        if (groupUID) {
          try {
            const cartResponse = await fetch('/cart.js');
            const cart = await cartResponse.json();

            const itemsToRemove = cart.items.filter(item => {
              const metadata = ProductPersonalisation.getItemMetadata(item);
              return metadata.groupUID === groupUID;
            });

            if (itemsToRemove.length === 0) {
              window.location.href = link.href;
              return;
            }

            await ProductPersonalisation.removeGroupItems(groupUID);

            if (window.location.pathname.includes('/cart')) {
              window.location.href = window.location.href.split('?')[0];
            } else {
              document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
              document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
              setTimeout(() => {
                ProductPersonalisation.initCartRemoval();
              }, 300);
            }
          } catch (err) {
            window.location.href = link.href;
          }
        } else {
          window.location.href = link.href;
        }
      };

      document._cartQtyHandler = async (e) => {
        const minusButton = e.target.closest('.js-qty__adjust--minus');
        if (!minusButton) return;

        const wrapper = minusButton.closest('.js-qty__wrapper');
        if (!wrapper) return;

        const input = wrapper.querySelector('.js-qty__num');
        if (!input) return;

        const itemKey = input.getAttribute('data-id');
        if (!itemKey) return;

        const currentQty = parseInt(input.value) || 1;

        if (currentQty === 1) {
          const cartItem = document.querySelector(`[data-key="${itemKey}"]`);
          if (!cartItem) return;

          const groupUID = cartItem.getAttribute('data-group-uid');
          const isMain = cartItem.classList.contains('cart__item--main');

          if (isMain && groupUID) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();

            await ProductPersonalisation.removeGroupItems(groupUID);
          }
        }
      };

      document.addEventListener('click', document._cartRemoveHandler, true);
      document.addEventListener('click', document._cartQtyHandler, true);
    },

    clearCart: async function() {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const removePromises = cart.items.map(item => {
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: item.key,
              quantity: 0
            })
          });
        });

        await Promise.all(removePromises);

        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        } else {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        }

      } catch (err) {
        alert('Error clearing cart. Please try again.');
      }
    },

    removeGroupItems: async function(groupUID) {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const itemsToRemove = cart.items.filter(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          return metadata.groupUID === groupUID;
        });

        if (itemsToRemove.length === 0) return;

        for (const item of itemsToRemove) {
          try {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (err) {
            // Silently continue
          }
        }

        await new Promise(resolve => setTimeout(resolve, 800));

        if (window.location.pathname.includes('/cart')) {
          window.location.href = window.location.href.split('?')[0];
        } else {
          document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          setTimeout(() => {
            ProductPersonalisation.initCartRemoval();
          }, 300);
        }
      } catch (err) {
        // Error handled silently
      }
    },

    cleanupOrphanedAddons: async function() {
      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        const groupUIDs = new Set();

        cart.items.forEach(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          if (metadata.isMain && metadata.groupUID) {
            groupUIDs.add(metadata.groupUID);
          }
        });

        const orphanedAddons = cart.items.filter(item => {
          const metadata = ProductPersonalisation.getItemMetadata(item);
          return metadata.isAddon && metadata.groupUID && !groupUIDs.has(metadata.groupUID);
        });

        if (orphanedAddons.length > 0) {
          const removePromises = orphanedAddons.map(item => {
            return fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                quantity: 0
              })
            });
          });

          await Promise.all(removePromises);
          await new Promise(resolve => setTimeout(resolve, 300));

          if (window.location.pathname.includes('/cart')) {
            window.location.reload();
          } else {
            document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
          }
        }
      } catch (err) {
        // Error handled silently
      }
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    ProductPersonalisation.init();

    setTimeout(() => {
      ProductPersonalisation.initCartRemoval();
      ProductPersonalisation.cleanupOrphanedAddons();
    }, 500);

    window.clearCart = () => {
      if (confirm('Are you sure you want to clear the entire cart? This cannot be undone.')) {
        ProductPersonalisation.clearCart();
      }
    };

    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        ProductPersonalisation.init();
        ProductPersonalisation.initCartRemoval();
      }, 100);
    });

    document.addEventListener('cart:build', () => {
      setTimeout(() => {
        ProductPersonalisation.sortCartItems();
        ProductPersonalisation.initCartRemoval();
        ProductPersonalisation.cleanupOrphanedAddons();
      }, 200);
    });
  });

})();
